import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { config } from '@/config/config';
import { env } from '@/env';
import { Version } from '@/modules/backend-version/components';
import { AckeeLogo, Heading, Icon, Link, Logo, Text } from '@/modules/ui';
import { ErrorCode } from '@/services/utilities/isApiError';

import { DemoLoginForm } from '../DemoLoginForm';
import { LoginButton } from '../LoginButton';
import { useErrorCodeParam } from './hooks/useErrorCodeParam';
import * as felaRules from './Login.rules';
import { LoginErrorAlert } from './LoginErrorAlert';
import { LoginErrorMessage } from './LoginErrorMessage';
import { RegisterButton } from './RegisterButton';

const isClientInstance = !env.VITE_DEMO && !env.VITE_SAAS;
const isSaas = env.VITE_SAAS;

export const Login = () => {
    const { css } = useFela();
    const errorCode = useErrorCodeParam();

    return (
        <div className={css(felaRules.container)} data-testid='login-page'>
            <header>
                <Logo customStyle={felaRules.logo} />
            </header>
            <main className={css(felaRules.wrapper)}>
                <Heading elementType='h1' color='black'>
                    <FormattedMessage
                        id={config.demo.enable ? 'auth.login.title.demo' : 'auth.login.title'}
                        values={{ brand: chunks => <span className={css(felaRules.brand)}>{chunks}</span> }}
                    />
                </Heading>

                {errorCode !== undefined && (
                    <>
                        <LoginErrorMessage errorCode={errorCode} />

                        {errorCode !== ErrorCode.AUTH_CLIENT_IS_NOT_CONFIGURED && (
                            <>
                                <Text>
                                    <FormattedMessage id={env.VITE_DEMO ? 'auth.login.desc.demo' : 'auth.login.desc'} />
                                </Text>
                                {env.VITE_DEMO ? <DemoLoginForm /> : <LoginButton />}
                                {isSaas ? <RegisterButton /> : null}
                            </>
                        )}

                        <LoginErrorAlert errorCode={errorCode} />
                    </>
                )}
            </main>
            <footer className={css(felaRules.infoWrapper)}>
                <Text variant='ParagraphSmallMedium' color='foregroundsTertiary' customStyle={felaRules.info}>
                    <span>
                        <FormattedMessage id='auth.login.copyright' />
                        <AckeeLogo />
                    </span>

                    <Version customStyle={felaRules.version} />
                    {isClientInstance ? (
                        <Link href={config.links.emergency} target='_blank' type='native' inheritColor size='regular'>
                            <span className={css(felaRules.emergency)}>
                                <Icon width={20} height={20} type='warning-triangle' />
                                <Icon width={10} height={10} type='settings' customStyle={felaRules.emergencySettingsIcon} />
                            </span>
                            <FormattedMessage id='auth.login.emergency' />
                        </Link>
                    ) : null}

                    {isSaas ? (
                        <Link href={config.links.privacyPolicy} target='_blank' type='native' inheritColor size='regular'>
                            <FormattedMessage id='auth.login.privacyPolicy' />
                        </Link>
                    ) : null}
                </Text>
            </footer>
        </div>
    );
};
